import React, { useContext, useEffect } from 'react';
import { Form } from 'src/core/forms';
import { BasketContext, BasketItemType } from 'src/components/Basket/BasketContext';
import { Button } from 'src/components/Button/Button';
import classes from 'src/components/Basket/BasketItemActions.scss';
import classnames from 'classnames';

import { allowOnlyNumbers } from '../../util/forms';
import { useFormQuantity } from '../Basket/upsell/useFormQuantity';

type Props = {
    isPending: boolean;
    item: BasketItemType;
};

export const BasketItemActions: React.FC<Props> = ({ isPending, item }) => {
    const { cartItem } = item;

    return (
        <div className={classes.basketItemActionsContent}>
            <Form initialValues={{ qty: cartItem.quantity }}>
                <fieldset disabled={isPending}>
                    <FormInner item={item} isPending={isPending} />
                </fieldset>
            </Form>
        </div>
    );
};

export const FormInner: React.FC<Props> = (props) => {
    const {
        item: { cartItem },
    } = props;
    const { updateItems, itemMessages, deleteItem } = useContext(BasketContext);
    const { quantity, oldQuantity, onReset, onInputChange, setQuantity } = useFormQuantity(cartItem.quantity);

    /**
     * `open` (controls visibility of the "Update / Reset" buttons) used to be
     * its own `useState` driven by `setOpen(false)` and `setOpen(true)` calls
     * in event handlers plus a `setOpen(false)` directly in the render body
     * whenever `quantity === oldQuantity`. Under React 19 + react-redux 9 the
     * render-time setState produced the "Cannot update a component while
     * rendering a different component" warning and then "Too many re-renders"
     * in <FormInner>, which the error boundary unmounted into the white
     * /basket screen. The state was always 1:1 with `quantity !== oldQuantity`
     * so deriving it removes the bug class entirely (no setState during render
     * needed).
     */
    const open = quantity !== oldQuantity;

    /**
     * Previously this lived directly in the render body
     * (`if (itemMessages?.[0]?.type === 'Error') setQuantity(oldQuantity)`),
     * which is a setState-during-render side effect. Moving it into an effect
     * keeps the same UX (server rejected the qty change → revert input) but
     * runs after commit, avoiding render-phase state churn.
     */
    useEffect(() => {
        if (itemMessages?.[0]?.type === 'Error') {
            setQuantity(oldQuantity);
        }
    }, [itemMessages, oldQuantity, setQuantity]);

    useEffect(() => {
        setQuantity(cartItem.quantity);
    }, [cartItem.quantity, setQuantity]);

    const update = () => {
        if (quantity === 0) {
            deleteItem({ itemId: Number(cartItem.id)! });
        } else {
            updateItems([{ itemId: parseInt(cartItem.id), qty: quantity }]);
        }
    };

    const reset = () => {
        onReset();
    };

    const add = () => {
        setQuantity((v) => v + 1);
    };

    const minus = () => {
        setQuantity((v) => v - 1);
    };

    const change = (e: React.ChangeEvent<HTMLInputElement>) => {
        onInputChange(e, 1);
    };

    return (
        <div className={classes.updateActionsWrapper}>
            <div
                className={classnames(classes.updateActionsInputAndBtns, {
                    [classes.updateActionsInputAndBtnsLarge]: quantity > 9999,
                })}
            >
                <Button onClick={minus} variant="light" className={classes.sizesVerticalBtn} disabled={quantity <= 1}>
                    -
                </Button>
                <input value={quantity} onKeyPress={allowOnlyNumbers} onChange={(e) => change(e)} type="number" />
                <Button onClick={add} variant="light" className={classes.sizesVerticalBtn}>
                    +
                </Button>
            </div>
            {open && (
                <div className={classes.updateActions}>
                    <button type="button" onClick={update} className={classes.updateBtn}>
                        Update
                    </button>
                    <button type="button" onClick={reset} className={classes.resetBtn}>
                        Reset to {oldQuantity}
                    </button>
                </div>
            )}
        </div>
    );
};
